/**
 * Vue-Sticky
 *
 * Directive adds behaviour similar to css position:sticky to an element;
 * this is not a polyfill, because position:sticky currently lacks the possibility
 * to distinguish between “sticky“ and “non-sticky“ state of an element;
 * this directive will add a css class whenever an element is positioned fixed
 *
 * Inspired by rguanghui/vue-sticky (less options, ES5 syntax)
 * @see https://github.com/rguanghui/vue-sticky
 *
 * @example
 * The following html
 *
 *      <div v-sticky>
 *          <div>…</div>
 *      </div>
 *
 * will turn into
 *
 *      <div v-sticky style="height: 50px;">
 *          <div class="fixed" style="position:fixed;">…</div>
 *      </div>
 *
 * when the elements top touches the upper viewport edge;
 * the height on the element is set before it is positioned fixed to prevent reflow within the browser
 */

import throttle from 'lodash/throttle';

/**
 * default css class that is set when the element is positioned fixed
 * @type {string}
 */
const FIXED_CLASS = 'fixed';


/**
 * Map is used to store listener functions created by the bind hook in the directive; they will be
 * retrieved when the removeScrollListener function is called; the DOM element is used as the key
 * @type {WeakMap}
 */
let scrollHandler = new WeakMap();

/**
 * directive definition
 */
export default {

    bind(el, binding) {
        let elStyle = el.style,
            child = el.firstElementChild,
            childStyle = child.style,
            sticky = false,
            fixedClass = FIXED_CLASS,
            listener;

        if (binding.value && binding.value.fixedClass) {
            fixedClass = binding.value.fixedClass;
        }

        function stick() {
            if (!elStyle.height) {
                elStyle.height = el.offsetHeight + 'px';
            }
            if (childStyle) {
                childStyle.position = 'fixed';
                el.classList.add(fixedClass);
            }
            sticky = true;
        }

        function unStick() {
            if (sticky && childStyle) {
                childStyle.position = '';
                el.classList.remove(fixedClass);
            }
            sticky = false;
        }

        listener = throttle(() => {
            if (el.getBoundingClientRect().top < 0) {
                stick();
            } else {
                unStick();
            }
        }, 100);

        window.addEventListener('scroll', listener);
        scrollHandler.set(el, listener);
    },

    unbind(el) {
        window.removeEventListener('scroll', scrollHandler.get(el));
    }

};